// Thin wrapper around window.Telegram.WebApp (injected by https://telegram.org/js/telegram-web-app.js,
// loaded in index.html). Every accessor here tolerates the SDK being absent -- e.g. during local
// dev in a plain browser, or if this page is ever opened outside Telegram -- by no-op'ing instead
// of throwing, so the rest of the app doesn't need to guard every call site itself.
// The SDK defines window.Telegram.WebApp in every browser, and in a plain one its popup, link and download methods throw or
// post to nowhere, so the helpers that open something use the native call only when `isInsideTelegram()` and a web
// equivalent otherwise.
import { webChooseDialog } from '@/webDialog'

function getWebApp() {
  return typeof window === 'undefined' ? undefined : window.Telegram?.WebApp
}

// The SDK script defines window.Telegram.WebApp in every browser, so its presence says nothing: a Telegram client has
// initData or reports a platform, a plain browser reports 'unknown' (and no initData).
export function isInsideTelegram(): boolean {
  const webApp = getWebApp()
  if (!webApp) return false
  return Boolean(webApp.initData) || (Boolean(webApp.platform) && webApp.platform !== 'unknown')
}

export function getInitData(): string {
  return getWebApp()?.initData ?? ''
}

// Telegram client the Mini App runs in ("ios", "android", "tdesktop", "weba", ... or "unknown" for a plain browser), for error reports.
export function getTelegramPlatform(): string {
  return getWebApp()?.platform || 'none'
}

export function getTelegramVersion(): string {
  return getWebApp()?.version || 'none'
}

// Language of the user's Telegram client (e.g. "de", "pt-br"), if known.
export function getTelegramLanguage(): string | null {
  const user = getWebApp()?.initDataUnsafe?.user as { language_code?: string } | undefined
  return user?.language_code ?? null
}

// Start parameter of the Mini App link (`?startapp=<param>`), if any.
export function getStartParam(): string | null {
  const param = getWebApp()?.initDataUnsafe?.start_param
  return typeof param === 'string' && param ? param : null
}

export function ready(): void {
  getWebApp()?.ready()
}

export function expand(): void {
  getWebApp()?.expand()
}

const THEME_VAR_MAPPING: Record<string, string> = {
  bg_color: '--tg-bg-color',
  text_color: '--tg-text-color',
  hint_color: '--tg-hint-color',
  link_color: '--tg-link-color',
  button_color: '--tg-button-color',
  button_text_color: '--tg-button-text-color',
  secondary_bg_color: '--tg-secondary-bg-color',
  header_bg_color: '--tg-header-bg-color',
  accent_text_color: '--tg-accent-text-color',
  section_bg_color: '--tg-section-bg-color',
  section_header_text_color: '--tg-section-header-text-color',
  destructive_text_color: '--tg-destructive-text-color',
}

function applyThemeParamsOnce(webApp: NonNullable<ReturnType<typeof getWebApp>>): void {
  const root = document.documentElement
  root.dataset.colorScheme = webApp.colorScheme

  const theme = webApp.themeParams
  for (const [key, cssVar] of Object.entries(THEME_VAR_MAPPING)) {
    const value = theme[key as keyof typeof theme]
    if (value) root.style.setProperty(cssVar, value)
  }
}

// Maps Telegram.WebApp.themeParams onto CSS custom properties so the page matches the user's
// Telegram theme (light/dark, accent color) instead of a fixed palette, and keeps them in sync
// if the user switches theme while the Mini App is open. Falls back to the existing :root
// defaults (see main.css) when not running inside Telegram. Idempotent -- safe to call more than
// once (e.g. on hot reload) since it registers the change listener only the first time.
let themeListenerRegistered = false

export function applyThemeParams(): void {
  const webApp = getWebApp()
  if (!webApp) return

  applyThemeParamsOnce(webApp)

  if (!themeListenerRegistered) {
    webApp.onEvent('themeChanged', () => applyThemeParamsOnce(webApp))
    themeListenerRegistered = true
  }
}

// Telegram's BackButton is one shared button (and the desktop client maps Esc to it), so the handlers form a stack: a
// click only runs the topmost one. A page registers its navigation, an overlay on top of it (the floating add box)
// registers its own close and the page's handler waits until the overlay is gone.
const backHandlers: (() => void)[] = []

function dispatchBack(): void {
  backHandlers[backHandlers.length - 1]?.()
}

export function showBackButton(onClick: () => void): () => void {
  const button = getWebApp()?.BackButton
  if (!button) return () => {}
  // (a wrapper, so the same function registered twice is two entries)
  const handler = (): void => onClick()
  if (backHandlers.length === 0) {
    button.onClick(dispatchBack)
    button.show()
  }
  backHandlers.push(handler)
  return () => {
    const index = backHandlers.indexOf(handler)
    if (index === -1) return
    backHandlers.splice(index, 1)
    if (backHandlers.length === 0) {
      button.offClick(dispatchBack)
      button.hide()
    }
  }
}

export function hapticSelection(): void {
  getWebApp()?.HapticFeedback?.selectionChanged()
}

export function hapticSuccess(): void {
  getWebApp()?.HapticFeedback?.notificationOccurred('success')
}

export function hapticError(): void {
  getWebApp()?.HapticFeedback?.notificationOccurred('error')
}

export function confirmDialog(message: string): Promise<boolean> {
  const webApp = getWebApp()
  if (isInsideTelegram() && webApp?.showConfirm) {
    return new Promise((resolve) => webApp.showConfirm!(message, resolve))
  }
  return Promise.resolve(window.confirm(message))
}

export interface ChoiceButton {
  id: string
  text: string
  destructive?: boolean
}

// Up to three buttons in Telegram's native popup (its own limit); outside Telegram, an in-page dialog
// (`webDialog.ts`). Resolves to the chosen id, or null when cancelled.
export function chooseDialog(message: string, choices: ChoiceButton[]): Promise<string | null> {
  const webApp = getWebApp()
  if (isInsideTelegram() && webApp?.showPopup) {
    const buttons: TelegramPopupButton[] = [
      ...choices.map((c) => ({
        id: c.id,
        text: c.text,
        type: c.destructive ? ('destructive' as const) : ('default' as const),
      })),
      { type: 'cancel' as const },
    ].slice(0, 3)
    return new Promise((resolve) =>
      webApp.showPopup!({ message, buttons }, (id) => resolve(id || null)),
    )
  }
  return webChooseDialog(message, choices)
}

// Telegram's native download popup (Bot API 8.0+); older clients get the external browser, which saves
// the file because the server answers with Content-Disposition: attachment. Needs a plain URL, no bearer token.
// A plain browser follows a temporary download link, so no empty tab opens for it.
export function downloadFileOrOpen(url: string, fileName: string): void {
  const webApp = getWebApp()
  if (!isInsideTelegram()) {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()
    return
  }
  if (webApp?.downloadFile && webApp.isVersionAtLeast?.('8.0')) {
    webApp.downloadFile({ url, file_name: fileName })
    return
  }
  openExternalLink(url)
}

export function openExternalLink(url: string): void {
  const webApp = getWebApp()
  if (isInsideTelegram() && webApp?.openLink && /^https?:/i.test(url)) {
    webApp.openLink(url)
    return
  }
  window.open(url, '_blank', 'noopener')
}

// A link from a todo's markdown: `t.me` links open inside Telegram, other web links in the external browser, and
// `tg://` / `mailto:` hand over to the OS (unverified: whether every client's webview follows a `tg://` window.open).
export function openMarkdownLink(url: string): void {
  const webApp = getWebApp()
  if (isInsideTelegram() && /^https?:\/\/(?:t|telegram)\.me\//i.test(url) && webApp?.openTelegramLink) {
    webApp.openTelegramLink(url)
    return
  }
  if (/^https?:/i.test(url)) {
    openExternalLink(url)
    return
  }
  window.open(url, '_blank', 'noopener')
}
